import React, { useState, useEffect } from "react";

const BASE_URL = "https://api.visiontrackcrm.com";

export default function StoreList() {
  const [activeTab, setActiveTab] = useState("ecommerce"); // 'ecommerce' | 'stores'
  const [search, setSearch] = useState("");
  const [stores, setStores] = useState([]);
  const [ecommerceUsers, setEcommerceUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ecommFilter, setEcommFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allStaff, setAllStaff] = useState([]);

  // Edit Store Modal State
  const [editingStore, setEditingStore] = useState(null);
  const [editStoreType, setEditStoreType] = useState("store"); // 'store' | 'ecommerce'
  const [editForm, setEditForm] = useState({
    store_name: "",
    owner_name: "",
    mobile: "",
    email: "",
    address: "",
    gst_number: "",
    plan_name: "",
    amount: "",
    expiry_date: "",
    subscription_status: "ACTIVE",
    top_order: "999",
    password: "",
  });
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchStores();
    fetchEcommerceUsers();
    fetchAllStaff();
  }, []);

  const normalizePhone = (val) => {
    const digits = String(val || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const normalizeEmail = (val) => String(val || "").trim().toLowerCase();

  const fetchAllStaff = async (storeListOverride = null) => {
    try {
      const res = await fetch(`${BASE_URL}/staff/all`);
      if (res.ok) {
        const data = await res.json();
        if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
          setAllStaff(data.data);
          return data.data;
        }
      }
    } catch (e) {
      // Fallback below
    }

    // Fallback: fetch staff per store if /staff/all is not available yet
    try {
      const targetStores = Array.isArray(storeListOverride) && storeListOverride.length > 0 ? storeListOverride : stores;
      if (!targetStores || targetStores.length === 0) return [];
      const results = await Promise.all(
        targetStores
          .filter((s) => s.store_code)
          .map((s) =>
            fetch(`${BASE_URL}/staff/${encodeURIComponent(s.store_code)}`)
              .then((r) => r.json())
              .then((d) => (d?.success && Array.isArray(d.data) ? d.data : []))
              .catch(() => [])
          )
      );
      const merged = results.flat();
      setAllStaff(merged);
      return merged;
    } catch (err) {
      console.log("FETCH ALL STAFF FALLBACK ERROR:", err);
      return [];
    }
  };

  const fetchStores = async () => {
    try {
      const response = await fetch(`${BASE_URL}/registration/stores`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setStores(result.data);
        fetchAllStaff(result.data);
      }
    } catch (error) {
      console.log("FETCH STORES ERROR:", error);
    }
  };

  const fetchEcommerceUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/ecommerce/users`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setEcommerceUsers(result.data);
      } else {
        setEcommerceUsers([]);
      }
    } catch (error) {
      console.log("FETCH ECOMMERCE USERS ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStoreRank = async (storeId, rank) => {
    try {
      const response = await fetch(`${BASE_URL}/registration/stores/top-order/${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topOrder: rank, isTop: rank < 999 }),
      });
      const data = await response.json();
      if (data?.success) {
        await fetchStores();
      } else {
        alert(data?.message || "Failed to update store rank.");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating store ranking.");
    }
  };

  const handleUpdateEcommerceRank = async (userId, rank) => {
    try {
      const response = await fetch(`${BASE_URL}/ecommerce/users/top-order/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topOrder: rank, isTop: rank < 999 }),
      });
      const data = await response.json();
      if (data?.success) {
        await fetchEcommerceUsers();
      } else {
        alert(data?.message || "Failed to update ranking.");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating wholesale ranking.");
    }
  };

  const handleUpdateEcommerceStatus = async (userId, newStatus) => {
    const actionName =
      newStatus === "APPROVED"
        ? "approve and activate"
        : newStatus === "REJECTED"
        ? "reject"
        : "deactivate";

    const confirm = window.confirm(
      `Are you sure you want to ${actionName} this E-Commerce store account?`
    );
    if (!confirm) return;

    try {
      setActionLoading(userId);
      const response = await fetch(`${BASE_URL}/ecommerce/users/status/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();

      if (data?.success) {
        alert(`Account successfully ${newStatus.toLowerCase()}!`);
        setSelectedUser(null);
        await fetchEcommerceUsers();
      } else {
        alert(data?.message || "Failed to update account status.");
      }
    } catch (err) {
      console.error(err);
      alert("Server error while updating status.");
    } finally {
      setActionLoading(null);
    }
  };

  // Validate Phone & Email Uniqueness across all stores, wholesale accounts, and all store staff
  const verifyPhoneAndEmailUniqueness = async ({
    mobile,
    email,
    excludeStoreId = null,
    excludeEcommerceId = null,
    excludeStoreCode = null,
    scope = "store",
  }) => {
    const cleanMobile = mobile ? normalizePhone(mobile) : "";
    const cleanEmail = email ? normalizeEmail(email) : "";

    if (!cleanMobile && !cleanEmail) {
      return { isUnique: true };
    }

    if (scope === "ecommerce") {
      // Check Wholesale E-Commerce Accounts
      for (const ec of ecommerceUsers) {
        if (excludeEcommerceId !== null && String(ec.id) === String(excludeEcommerceId)) continue;
        if (
          excludeStoreCode &&
          ec.store_code &&
          String(ec.store_code).trim().toUpperCase() === String(excludeStoreCode).trim().toUpperCase()
        ) {
          continue;
        }

        const ecMobile = normalizePhone(ec.mobile);
        const ecEmail = normalizeEmail(ec.email);

        if (cleanMobile && ecMobile && cleanMobile === ecMobile) {
          return {
            isUnique: false,
            message: `Mobile number "${mobile}" is already registered with wholesale store "${ec.business_name || ec.store_code}" (${ec.store_code}). Duplicate phone numbers are not allowed.`,
          };
        }
        if (cleanEmail && ecEmail && cleanEmail === ecEmail) {
          return {
            isUnique: false,
            message: `Email address "${email}" is already registered with wholesale store "${ec.business_name || ec.store_code}" (${ec.store_code}). Duplicate emails are not allowed.`,
          };
        }
      }
      return { isUnique: true };
    }

    // 1. Check all Optical Clinic Stores
    for (const st of stores) {
      if (excludeStoreId !== null && String(st.id) === String(excludeStoreId)) continue;
      if (
        excludeStoreCode &&
        st.store_code &&
        String(st.store_code).trim().toUpperCase() === String(excludeStoreCode).trim().toUpperCase()
      ) {
        continue;
      }

      const stMobile = normalizePhone(st.mobile);
      const stEmail = normalizeEmail(st.email);

      if (cleanMobile && stMobile && cleanMobile === stMobile) {
        return {
          isUnique: false,
          message: `Mobile number "${mobile}" is already registered with another store "${st.store_name || st.store_code}" (${st.store_code || "ID: " + st.id}). Duplicate phone numbers are not allowed.`,
        };
      }
      if (cleanEmail && stEmail && cleanEmail === stEmail) {
        return {
          isUnique: false,
          message: `Email address "${email}" is already registered with another store "${st.store_name || st.store_code}" (${st.store_code || "ID: " + st.id}). Duplicate emails are not allowed.`,
        };
      }
    }

    // 2. Check all Staff Members across all stores
    let staffToCheck = allStaff;
    if (!Array.isArray(staffToCheck) || staffToCheck.length === 0) {
      staffToCheck = await fetchAllStaff(stores);
    }

    for (const stf of staffToCheck) {
      const stfPhone = normalizePhone(stf.phone || stf.mobile);
      const stfEmail = normalizeEmail(stf.email);

      if (cleanMobile && stfPhone && cleanMobile === stfPhone) {
        return {
          isUnique: false,
          message: `Mobile number "${mobile}" is already registered to staff member "${stf.name}" (Store: ${stf.store_code}). Store mobile number cannot match any store staff phone number.`,
        };
      }
      if (cleanEmail && stfEmail && cleanEmail === stfEmail) {
        return {
          isUnique: false,
          message: `Email address "${email}" is already registered to staff member "${stf.name}" (Store: ${stf.store_code}). Store email cannot match any store staff email.`,
        };
      }
    }

    // 3. Also verify against backend /registration/check-unique endpoint
    try {
      const res = await fetch(`${BASE_URL}/registration/check-unique`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: cleanMobile || null,
          email: cleanEmail || null,
          excludeStoreId,
          excludeEcommerceId,
          excludeStoreCode,
          scope,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.isUnique === false) {
          return {
            isUnique: false,
            message: data.message || "Mobile number or email address is already registered with another store or staff member.",
          };
        }
      }
    } catch (e) {
      // Client-side check already verified stores and allStaff
    }

    return { isUnique: true };
  };

  // Open Edit Modal for Optical Clinic Store or Wholesale E-Commerce Store
  const handleOpenEditModal = (item, type = "store") => {
    setEditError("");
    setEditStoreType(type);
    setEditingStore(item);

    let formattedExpiry = "";
    if (item.expiry_date) {
      try {
        const d = new Date(item.expiry_date);
        if (!isNaN(d.getTime())) {
          formattedExpiry = d.toISOString().split("T")[0];
        }
      } catch (e) {}
    }

    setEditForm({
      store_name: item.store_name || item.business_name || "",
      owner_name: item.owner_name || "",
      mobile: item.mobile || "",
      email: item.email || "",
      address: item.address || "",
      gst_number: item.gst_number || "",
      plan_name: item.plan_name || "Starter Plan",
      amount: item.amount !== undefined && item.amount !== null ? String(item.amount) : "0",
      expiry_date: formattedExpiry,
      subscription_status:
        type === "ecommerce"
          ? String(item.status || "PENDING").toUpperCase()
          : String(item.subscription_status || item.status || "ACTIVE").toUpperCase(),
      top_order: item.top_order && item.top_order < 999 ? String(item.top_order) : "999",
      password: "",
    });
  };

  // Save Edited Store Details
  const handleSaveEditStore = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!editingStore) return;

    setEditError("");
    const cleanStoreName = String(editForm.store_name || "").trim();
    const cleanOwnerName = String(editForm.owner_name || "").trim();
    const cleanMobile = String(editForm.mobile || "").trim();
    const cleanEmail = String(editForm.email || "").trim().toLowerCase();

    if (!cleanStoreName || !cleanOwnerName || !cleanMobile || !cleanEmail) {
      const msg = "Store Name, Owner Name, Mobile Number, and Email Address are required.";
      setEditError(msg);
      return;
    }

    const mobileDigits = normalizePhone(cleanMobile);
    if (mobileDigits.length !== 10) {
      const msg = "Please enter a valid 10-digit mobile number.";
      setEditError(msg);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      const msg = "Please enter a valid email address.";
      setEditError(msg);
      return;
    }

    try {
      setEditSaving(true);

      // Check if phone or email was actually modified
      const currentMobile = normalizePhone(editingStore.mobile);
      const currentEmail = normalizeEmail(editingStore.email);
      const isMobileChanged = mobileDigits !== currentMobile;
      const isEmailChanged = cleanEmail !== currentEmail;

      // ONLY verify uniqueness if the user is ACTUALLY changing the phone number or email address!
      // If other store details are being updated (name, owner, address, plan, amount, status, rank, etc.), do not block with phone/email conflict!
      if (isMobileChanged || isEmailChanged) {
        const uniqueness = await verifyPhoneAndEmailUniqueness({
          mobile: isMobileChanged ? mobileDigits : null,
          email: isEmailChanged ? cleanEmail : null,
          excludeStoreId: editStoreType === "store" ? editingStore.id : null,
          excludeEcommerceId: editStoreType === "ecommerce" ? editingStore.id : null,
          excludeStoreCode: editingStore.store_code,
          scope: editStoreType,
        });

        if (!uniqueness.isUnique) {
          setEditError(uniqueness.message);
          alert(uniqueness.message);
          setEditSaving(false);
          return;
        }
      }

      const endpoint =
        editStoreType === "ecommerce"
          ? `${BASE_URL}/ecommerce/users/${editingStore.id}`
          : `${BASE_URL}/registration/stores/${editingStore.id}`;

      const payload =
        editStoreType === "ecommerce"
          ? {
              business_name: cleanStoreName,
              owner_name: cleanOwnerName,
              mobile: mobileDigits,
              email: cleanEmail,
              address: String(editForm.address || "").trim(),
              gst_number: String(editForm.gst_number || "").trim(),
              status: editForm.subscription_status,
              top_order: Number(editForm.top_order || 999),
              is_top: Number(editForm.top_order || 999) < 999,
              ...(editForm.password && String(editForm.password).trim()
                ? { password: String(editForm.password).trim() }
                : {}),
            }
          : {
              store_name: cleanStoreName,
              owner_name: cleanOwnerName,
              mobile: mobileDigits,
              email: cleanEmail,
              address: String(editForm.address || "").trim(),
              plan_name: String(editForm.plan_name || "Starter Plan").trim(),
              amount: Number(editForm.amount || 0),
              expiry_date: editForm.expiry_date || editingStore.expiry_date,
              subscription_status: editForm.subscription_status,
              top_order: Number(editForm.top_order || 999),
              is_top: Number(editForm.top_order || 999) < 999,
              ...(editForm.password && String(editForm.password).trim()
                ? { password: String(editForm.password).trim() }
                : {}),
            };

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        const errMsg = data?.message || "Failed to update store details.";
        setEditError(errMsg);
        alert(errMsg);
        return;
      }

      alert("Store details updated successfully!");
      setEditingStore(null);
      await fetchStores();
      await fetchEcommerceUsers();
    } catch (err) {
      console.error("Save store edit error:", err);
      const errMsg = "Server error while updating store details: " + err.message;
      setEditError(errMsg);
      alert(errMsg);
    } finally {
      setEditSaving(false);
    }
  };

  // Delete Store Handler
  const handleDeleteStore = async (item, type = "store") => {
    const label = item.store_name || item.business_name || item.store_code;
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete store "${label}" (${item.store_code})?`
    );
    if (!confirmed) return;

    try {
      setActionLoading(item.id);
      const endpoint =
        type === "ecommerce"
          ? `${BASE_URL}/ecommerce/users/${item.id}`
          : `${BASE_URL}/registration/stores/${item.id}`;

      const response = await fetch(endpoint, {
        method: "DELETE",
      });
      const data = await response.json();

      if (response.ok && data?.success) {
        alert(`Store "${label}" deleted successfully.`);
        if (type === "ecommerce") {
          setEcommerceUsers((prev) => prev.filter((u) => u.id !== item.id));
          await fetchEcommerceUsers();
        } else {
          setStores((prev) => prev.filter((s) => s.id !== item.id));
          await fetchStores();
        }
      } else {
        alert(data?.message || "Failed to delete store.");
      }
    } catch (err) {
      console.error("Delete store error:", err);
      alert("Server error while deleting store.");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  // Filtered stores (Clinic App)
  const filteredStores = stores.filter((store) => {
    const text = search.toLowerCase();
    return (
      store.store_name?.toLowerCase().includes(text) ||
      store.owner_name?.toLowerCase().includes(text) ||
      store.mobile?.includes(text) ||
      store.store_code?.toLowerCase().includes(text)
    );
  });

  // Filtered ecommerce users
  const filteredEcommerceUsers = ecommerceUsers.filter((user) => {
    const statusUpper = (user.status || "PENDING").toUpperCase();
    if (ecommFilter !== "all" && statusUpper !== ecommFilter.toUpperCase()) {
      return false;
    }

    if (search.trim()) {
      const text = search.toLowerCase();
      return (
        user.business_name?.toLowerCase().includes(text) ||
        user.owner_name?.toLowerCase().includes(text) ||
        user.email?.toLowerCase().includes(text) ||
        user.mobile?.includes(text) ||
        user.store_code?.toLowerCase().includes(text)
      );
    }

    return true;
  });

  const pendingCount = ecommerceUsers.filter(
    (u) => (u.status || "PENDING").toUpperCase() === "PENDING"
  ).length;

  return (
    <div className="store-management-page">
      {/* HEADER SECTION */}
      <div className="header-section">
        <div>
          <h1 className="title">Store & Account Management</h1>
          <p className="subtitle">
            Manage physical optical clinic stores and approve Wholesale E-Commerce seller & buyer registrations.
          </p>
        </div>

        <button
          className="refresh-btn"
          onClick={() => {
            fetchStores();
            fetchEcommerceUsers();
          }}
          disabled={loading}
        >
          ↻ Refresh Accounts
        </button>
      </div>

      {/* TOP TAB SWITCHER */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === "ecommerce" ? "active" : ""}`}
          onClick={() => setActiveTab("ecommerce")}
        >
          🛒 Wholesale E-Commerce Accounts
          {pendingCount > 0 && <span className="pending-badge">{pendingCount} Pending</span>}
        </button>

        <button
          className={`tab-btn ${activeTab === "stores" ? "active" : ""}`}
          onClick={() => setActiveTab("stores")}
        >
          🏪 Optical Clinic Stores ({stores.length})
        </button>
      </div>

      {/* ========================================================= */}
      {/* E-COMMERCE ACCOUNTS TAB */}
      {/* ========================================================= */}
      {activeTab === "ecommerce" && (
        <div className="tab-content">
          {/* CONTROLS */}
          <div className="sub-controls">
            <div className="ecomm-filters">
              <button
                className={`filter-pill ${ecommFilter === "all" ? "active" : ""}`}
                onClick={() => setEcommFilter("all")}
              >
                All ({ecommerceUsers.length})
              </button>
              <button
                className={`filter-pill pending ${ecommFilter === "pending" ? "active" : ""}`}
                onClick={() => setEcommFilter("pending")}
              >
                Pending Activation ({pendingCount})
              </button>
              <button
                className={`filter-pill approved ${ecommFilter === "approved" ? "active" : ""}`}
                onClick={() => setEcommFilter("approved")}
              >
                Active & Approved (
                {
                  ecommerceUsers.filter(
                    (u) => (u.status || "").toUpperCase() === "APPROVED"
                  ).length
                }
                )
              </button>
              <button
                className={`filter-pill rejected ${ecommFilter === "rejected" ? "active" : ""}`}
                onClick={() => setEcommFilter("rejected")}
              >
                Rejected / Inactive (
                {
                  ecommerceUsers.filter((u) => {
                    const st = (u.status || "").toUpperCase();
                    return st === "REJECTED" || st === "INACTIVE";
                  }).length
                }
                )
              </button>
            </div>

            <input
              className="search-input"
              placeholder="Search by Business Name, Owner, Email, Phone, Code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* TABLE BOX */}
          <div className="table-box">
            <table>
              <thead>
                <tr>
                  <th>Store Code</th>
                  <th>Top Rank (Top 10)</th>
                  <th>Business / Trade Name</th>
                  <th>Authorized Owner</th>
                  <th>Contact Email & Phone</th>
                  <th>GST Number</th>
                  <th>Registered Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="table-msg">
                      <div className="mini-spinner"></div> Loading accounts...
                    </td>
                  </tr>
                ) : filteredEcommerceUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="table-msg">
                      No E-Commerce accounts found in this view.
                    </td>
                  </tr>
                ) : (
                  filteredEcommerceUsers.map((user) => {
                    const status = (user.status || "PENDING").toUpperCase();

                    return (
                      <tr key={user.id}>
                        <td>
                          <span className="code-tag">{user.store_code}</span>
                        </td>
                        <td>
                          <select
                            style={{
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: user.top_order < 999 ? "1.5px solid #2563EB" : "1px solid #CBD5E1",
                              backgroundColor: user.top_order < 999 ? "#EFF6FF" : "#FFFFFF",
                              fontWeight: user.top_order < 999 ? "700" : "500",
                              color: user.top_order < 999 ? "#1D4ED8" : "#334155",
                              cursor: "pointer",
                              fontSize: "12px"
                            }}
                            value={user.top_order && user.top_order < 999 ? String(user.top_order) : "999"}
                            onChange={(e) => handleUpdateEcommerceRank(user.id, e.target.value)}
                          >
                            <option value="999">Standard</option>
                            <option value="1">⭐ Top 1</option>
                            <option value="2">⭐ Top 2</option>
                            <option value="3">⭐ Top 3</option>
                            <option value="4">⭐ Top 4</option>
                            <option value="5">⭐ Top 5</option>
                            <option value="6">⭐ Top 6</option>
                            <option value="7">⭐ Top 7</option>
                            <option value="8">⭐ Top 8</option>
                            <option value="9">⭐ Top 9</option>
                            <option value="10">⭐ Top 10</option>
                          </select>
                        </td>
                        <td>
                          <strong>{user.business_name}</strong>
                          {user.address ? (
                            <div className="sub-addr" title={user.address}>
                              📍 {user.address}
                            </div>
                          ) : null}
                        </td>
                        <td>{user.owner_name}</td>
                        <td>
                          <div className="contact-cell">
                            <span>📧 {user.email}</span>
                            {user.mobile && <span>📞 {user.mobile}</span>}
                          </div>
                        </td>
                        <td>{user.gst_number || "—"}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <span className={`status-pill ${status.toLowerCase()}`}>
                            {status === "APPROVED" && "✅ Active"}
                            {status === "PENDING" && "⏳ Pending"}
                            {status === "REJECTED" && "❌ Rejected"}
                            {status === "INACTIVE" && "⛔ Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button
                              className="btn-details"
                              onClick={() => setSelectedUser(user)}
                            >
                              Details
                            </button>

                            {status === "PENDING" && (
                              <>
                                <button
                                  className="btn-approve"
                                  disabled={actionLoading === user.id}
                                  onClick={() =>
                                    handleUpdateEcommerceStatus(user.id, "APPROVED")
                                  }
                                >
                                  {actionLoading === user.id ? "..." : "Approve & Activate"}
                                </button>
                                <button
                                  className="btn-reject"
                                  disabled={actionLoading === user.id}
                                  onClick={() =>
                                    handleUpdateEcommerceStatus(user.id, "REJECTED")
                                  }
                                >
                                  Reject
                                </button>
                              </>
                            )}

                            {status === "APPROVED" && (
                              <button
                                className="btn-deactivate"
                                disabled={actionLoading === user.id}
                                onClick={() =>
                                  handleUpdateEcommerceStatus(user.id, "INACTIVE")
                                }
                              >
                                Deactivate
                              </button>
                            )}

                            {(status === "REJECTED" || status === "INACTIVE") && (
                              <button
                                className="btn-approve"
                                disabled={actionLoading === user.id}
                                onClick={() =>
                                  handleUpdateEcommerceStatus(user.id, "APPROVED")
                                }
                              >
                                Re-Activate
                              </button>
                            )}

                            <button
                              className="btn-edit-store"
                              onClick={() => handleOpenEditModal(user, "ecommerce")}
                            >
                              ✏️ Edit
                            </button>

                            <button
                              className="btn-delete-store"
                              disabled={actionLoading === user.id}
                              onClick={() => handleDeleteStore(user, "ecommerce")}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* OPTICAL CLINIC STORES TAB */}
      {/* ========================================================= */}
      {activeTab === "stores" && (
        <div className="tab-content">
          <div className="sub-controls">
            <input
              className="search-input"
              placeholder="Search store name / owner / mobile / email / store code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="table-box">
            <table>
              <thead>
                <tr>
                  <th>Store Code</th>
                  <th>Top Rank (Top 10)</th>
                  <th>Store Name</th>
                  <th>Owner</th>
                  <th>Mobile & Email</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {stores.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="table-msg">
                      No stores registered yet.
                    </td>
                  </tr>
                ) : (
                  filteredStores.map((store) => (
                    <tr key={store.id}>
                      <td>
                        <span className="code-tag">{store.store_code}</span>
                      </td>
                      <td>
                        <select
                          style={{
                            padding: "4px 8px",
                            borderRadius: "6px",
                            border: store.top_order < 999 ? "1.5px solid #2563EB" : "1px solid #CBD5E1",
                            backgroundColor: store.top_order < 999 ? "#EFF6FF" : "#FFFFFF",
                            fontWeight: store.top_order < 999 ? "700" : "500",
                            color: store.top_order < 999 ? "#1D4ED8" : "#334155",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          value={store.top_order && store.top_order < 999 ? String(store.top_order) : "999"}
                          onChange={(e) => handleUpdateStoreRank(store.id, e.target.value)}
                        >
                          <option value="999">Standard (No Rank)</option>
                          <option value="1">⭐ Top 1</option>
                          <option value="2">⭐ Top 2</option>
                          <option value="3">⭐ Top 3</option>
                          <option value="4">⭐ Top 4</option>
                          <option value="5">⭐ Top 5</option>
                          <option value="6">⭐ Top 6</option>
                          <option value="7">⭐ Top 7</option>
                          <option value="8">⭐ Top 8</option>
                          <option value="9">⭐ Top 9</option>
                          <option value="10">⭐ Top 10</option>
                        </select>
                      </td>
                      <td>
                        <strong>{store.store_name}</strong>
                        {store.address ? (
                          <div className="sub-addr" title={store.address}>
                            📍 {store.address}
                          </div>
                        ) : null}
                      </td>
                      <td>{store.owner_name}</td>
                      <td>
                        <div className="contact-cell">
                          <span>📞 {store.mobile || "-"}</span>
                          {store.email && <span style={{ color: "#64748b" }}>📧 {store.email}</span>}
                        </div>
                      </td>
                      <td>{store.plan_name || "Standard"}</td>
                      <td>₹ {store.amount || "0"}</td>
                      <td>{formatDate(store.expiry_date)}</td>
                      <td>
                        <span
                          className={`status-pill ${
                            store.subscription_status === "ACTIVE"
                              ? "approved"
                              : store.subscription_status === "DEACTIVATED" || store.subscription_status === "INACTIVE"
                              ? "rejected"
                              : store.subscription_status === "PENDING"
                              ? "pending"
                              : "expired"
                          }`}
                        >
                          {store.subscription_status || "PENDING"}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn-edit-store"
                            onClick={() => handleOpenEditModal(store, "store")}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn-delete-store"
                            disabled={actionLoading === store.id}
                            onClick={() => handleDeleteStore(store, "store")}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USER DETAILS MODAL */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedUser.business_name}</h2>
                <p>E-Commerce Account Credentials & Verification</p>
              </div>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="info-row">
                <span>Store Code:</span>
                <strong>{selectedUser.store_code}</strong>
              </div>
              <div className="info-row">
                <span>Owner Name:</span>
                <strong>{selectedUser.owner_name}</strong>
              </div>
              <div className="info-row">
                <span>Email Address:</span>
                <strong>{selectedUser.email}</strong> 
              </div>
              <div className="info-row">
                <span>Phone / WhatsApp:</span>
                <strong>{selectedUser.mobile || "—"}</strong>
              </div>
              <div className="info-row">
                <span>GST Identification:</span>
                <strong>{selectedUser.gst_number || "Not provided"}</strong>
              </div>
              <div className="info-row">
                <span>Warehouse Address:</span>
                <strong>{selectedUser.address || "Not provided"}</strong>
              </div>
              <div className="info-row">
                <span>Account Status:</span>
                <strong className={`status-pill ${selectedUser.status?.toLowerCase()}`}>
                  {selectedUser.status}
                </strong>
              </div>
              <div className="info-row">
                <span>Registered At:</span>
                <strong>{formatDate(selectedUser.created_at)}</strong>
              </div>
            </div>

            <div className="modal-footer">
              {selectedUser.status !== "APPROVED" && (
                <button
                  className="btn-approve"
                  onClick={() =>
                    handleUpdateEcommerceStatus(selectedUser.id, "APPROVED")
                  }
                >
                  Approve & Activate Account
                </button>
              )}
              {selectedUser.status !== "REJECTED" && selectedUser.status === "PENDING" && (
                <button
                  className="btn-reject"
                  onClick={() =>
                    handleUpdateEcommerceStatus(selectedUser.id, "REJECTED")
                  }
                >
                  Reject Account
                </button>
              )}
              {selectedUser.status === "APPROVED" && (
                <button
                  className="btn-deactivate"
                  onClick={() =>
                    handleUpdateEcommerceStatus(selectedUser.id, "INACTIVE")
                  }
                >
                  Deactivate Account
                </button>
              )}
              <button
                className="btn-close"
                onClick={() => setSelectedUser(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT STORE MODAL */}
      {editingStore && (
        <div className="modal-overlay" onClick={() => !editSaving && setEditingStore(null)}>
          <div className="detail-modal edit-store-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>
                  Edit {editStoreType === "ecommerce" ? "Wholesale Store" : "Optical Clinic Store"}{" "}
                  <span className="code-tag" style={{ marginLeft: 8 }}>
                    {editingStore.store_code}
                  </span>
                </h2>
                <p>
                  Update store details. Phone number & email are verified against all stores and store staff.
                </p>
              </div>
              <button
                className="close-btn"
                onClick={() => !editSaving && setEditingStore(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveEditStore}>
              <div className="modal-body edit-modal-body">
                {editError && (
                  <div className="edit-error-banner">
                    ⚠️ {editError}
                  </div>
                )}

                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>
                      {editStoreType === "ecommerce" ? "Business / Trade Name *" : "Store Name *"}
                    </label>
                    <input
                      type="text"
                      value={editForm.store_name}
                      onChange={(e) => setEditForm({ ...editForm, store_name: e.target.value })}
                      placeholder="Enter store name"
                      required
                    />
                  </div>

                  <div className="edit-field">
                    <label>Owner Name *</label>
                    <input
                      type="text"
                      value={editForm.owner_name}
                      onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                      placeholder="Enter owner name"
                      required
                    />
                  </div>

                  <div className="edit-field">
                    <label>Mobile Number (10 Digits) *</label>
                    <input
                      type="text"
                      maxLength={10}
                      value={editForm.mobile}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          mobile: e.target.value.replace(/[^0-9]/g, ""),
                        })
                      }
                      placeholder="10-digit mobile number"
                      required
                    />
                  </div>

                  <div className="edit-field">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="store@example.com"
                      required
                    />
                  </div>

                  {editStoreType === "store" ? (
                    <>
                      <div className="edit-field">
                        <label>Subscription Plan</label>
                        <input
                          type="text"
                          value={editForm.plan_name}
                          onChange={(e) => setEditForm({ ...editForm, plan_name: e.target.value })}
                          placeholder="Starter Plan"
                        />
                      </div>

                      <div className="edit-field">
                        <label>Plan Amount (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.amount}
                          onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                          placeholder="999"
                        />
                      </div>

                      <div className="edit-field">
                        <label>Expiry Date</label>
                        <input
                          type="date"
                          value={editForm.expiry_date}
                          onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                        />
                      </div>

                      <div className="edit-field">
                        <label>Subscription Status</label>
                        <select
                          value={editForm.subscription_status}
                          onChange={(e) =>
                            setEditForm({ ...editForm, subscription_status: e.target.value })
                          }
                        >
                          <option value="ACTIVE">ACTIVE</option>
                          <option value="PENDING">PENDING</option>
                          <option value="DEACTIVATED">DEACTIVATED</option>
                          <option value="EXPIRED">EXPIRED</option>
                          <option value="REJECTED">REJECTED</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="edit-field">
                        <label>GST Number</label>
                        <input
                          type="text"
                          value={editForm.gst_number}
                          onChange={(e) => setEditForm({ ...editForm, gst_number: e.target.value })}
                          placeholder="GSTIN"
                        />
                      </div>

                      <div className="edit-field">
                        <label>Account Status</label>
                        <select
                          value={editForm.subscription_status}
                          onChange={(e) =>
                            setEditForm({ ...editForm, subscription_status: e.target.value })
                          }
                        >
                          <option value="APPROVED">APPROVED (Active)</option>
                          <option value="PENDING">PENDING</option>
                          <option value="INACTIVE">INACTIVE</option>
                          <option value="REJECTED">REJECTED</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div className="edit-field">
                    <label>Top Rank (Top 10)</label>
                    <select
                      value={editForm.top_order}
                      onChange={(e) => setEditForm({ ...editForm, top_order: e.target.value })}
                    >
                      <option value="999">Standard (No Rank)</option>
                      <option value="1">⭐ Top 1</option>
                      <option value="2">⭐ Top 2</option>
                      <option value="3">⭐ Top 3</option>
                      <option value="4">⭐ Top 4</option>
                      <option value="5">⭐ Top 5</option>
                      <option value="6">⭐ Top 6</option>
                      <option value="7">⭐ Top 7</option>
                      <option value="8">⭐ Top 8</option>
                      <option value="9">⭐ Top 9</option>
                      <option value="10">⭐ Top 10</option>
                    </select>
                  </div>

                  <div className="edit-field">
                    <label>Reset Password (Optional)</label>
                    <input
                      type="password"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>

                  <div className="edit-field full-width">
                    <label>Store Address</label>
                    <textarea
                      rows={2}
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      placeholder="Full store address"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-close"
                  disabled={editSaving}
                  onClick={() => setEditingStore(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save-store" disabled={editSaving}>
                  {editSaving ? "Verifying & Saving..." : "Save Store Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .store-management-page {
          width: 100%;
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 16px;
        }

        .title {
          font-size: 24px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .subtitle {
          font-size: 13.5px;
          color: #64748b;
          margin: 4px 0 0 0;
        }

        .refresh-btn {
          background: #2563eb;
          color: white;
          border: none;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
        }

        .refresh-btn:hover {
          background: #1d4ed8;
        }

        /* TABS */
        .tabs-container {
          display: flex;
          gap: 10px;
          margin-bottom: 18px;
          border-bottom: 1px solid #cbd5e1;
          padding-bottom: 2px;
        }

        .tab-btn {
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-bottom: none;
          border-radius: 10px 10px 0 0;
          padding: 11px 20px;
          font-weight: 700;
          font-size: 13.5px;
          color: #475569;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.15s ease;
        }

        .tab-btn.active {
          background: #ffffff;
          color: #2563eb;
          border-color: #cbd5e1;
          border-bottom: 2px solid #2563eb;
          margin-bottom: -1px;
        }

        .pending-badge {
          background: #f59e0b;
          color: white;
          padding: 2px 8px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 800;
        }

        /* SUB CONTROLS */
        .sub-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .ecomm-filters {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filter-pill {
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 7px 14px;
          font-size: 12.5px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
        }

        .filter-pill.active {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
        }

        .search-input {
          width: 320px;
          height: 40px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 0 14px;
          font-size: 13px;
          outline: none;
        }

        .search-input:focus {
          border-color: #2563eb;
        }

        /* TABLE */
        .table-box {
          background: white;
          border-radius: 14px;
          overflow: auto;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        th {
          background: #f8fafc;
          color: #1e293b;
          padding: 13px 16px;
          text-align: left;
          font-weight: 700;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }

        td {
          padding: 13px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
        }

        tr:hover {
          background: #fafcff;
        }

        .code-tag {
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
          padding: 3px 8px;
          border-radius: 6px;
          font-weight: 800;
          font-size: 11.5px;
        }

        .sub-addr {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
          max-width: 220px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .contact-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 11.5px;
        }

        .status-pill {
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11.5px;
          font-weight: 800;
          display: inline-block;
        }

        .status-pill.approved { background: #dcfce7; color: #166534; }
        .status-pill.pending { background: #fef3c7; color: #92400e; }
        .status-pill.rejected { background: #fee2e2; color: #991b1b; }
        .status-pill.inactive { background: #f1f5f9; color: #475569; }
        .status-pill.expired { background: #fee2e2; color: #dc2626; }

        .actions-cell {
          display: flex;
          gap: 6px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-details {
          background: #f1f5f9;
          color: #334155;
          border: 1px solid #cbd5e1;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-approve {
          background: #16a34a;
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-approve:hover { background: #15803d; }

        .btn-reject {
          background: #dc2626;
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-reject:hover { background: #b91c1c; }

        .btn-deactivate {
          background: #f59e0b;
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-close {
          background: #f1f5f9;
          color: #334155;
          border: 1px solid #cbd5e1;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-edit-store {
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-edit-store:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .btn-delete-store {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-delete-store:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        .btn-save-store {
          background: #2563eb;
          color: white;
          border: none;
          padding: 8px 18px;
          border-radius: 6px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-save-store:hover {
          background: #1d4ed8;
        }

        .btn-save-store:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .edit-store-modal {
          max-width: 660px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }

        .edit-modal-body {
          max-height: 68vh;
          overflow-y: auto;
        }

        .edit-error-banner {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 600;
          margin-bottom: 14px;
          line-height: 1.4;
        }

        .edit-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .edit-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .edit-field.full-width {
          grid-column: 1 / -1;
        }

        .edit-field label {
          font-size: 12px;
          font-weight: 700;
          color: #334155;
        }

        .edit-field input,
        .edit-field select,
        .edit-field textarea {
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 9px 12px;
          font-size: 13px;
          color: #0f172a;
          outline: none;
          font-family: inherit;
        }

        .edit-field input:focus,
        .edit-field select:focus,
        .edit-field textarea:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }

        .table-msg {
          text-align: center;
          padding: 40px;
          color: #64748b;
          font-weight: 600;
        }

        .mini-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid #cbd5e1;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          vertical-align: middle;
          margin-right: 6px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* MODAL */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 9999;
        }

        .detail-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 540px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.2);
          overflow: hidden;
        }

        .modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          color: #0f172a;
        }

        .modal-header p {
          margin: 2px 0 0 0;
          font-size: 12px;
          color: #64748b;
        }

        .close-btn {
          border: none;
          background: #f1f5f9;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-body {
          padding: 20px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #f1f5f9;
          font-size: 13px;
        }

        .info-row span {
          color: #64748b;
        }

        .info-row strong {
          color: #0f172a;
          text-align: right;
          max-width: 60%;
        }

        .modal-footer {
          padding: 14px 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          background: #fafafa;
        }

        @media(max-width: 768px) {
          .header-section {
            flex-direction: column;
            align-items: flex-start;
          }
          .sub-controls {
            flex-direction: column;
            align-items: stretch;
          }
          .search-input {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}